import { policyListSchema, policyToggleSchema, policyToggleResultSchema, policySettingsSchema, policySettingsUpdateSchema, policySettingsUpdateResultSchema, policySettingsDeleteResultSchema, policyEditContextSchema, } from '../contracts/policies';
import { organizationIdSchema, } from '../contracts/organizations';
import { piiInfoTypesListSchema, } from '../contracts/pii-info-types';
import { parseSchema } from '../errors';
/**
 * List the DLP infoTypes available for the PII Detection policy picker
 * (KIR-3837). Server-side cached; overlays Kirin sensitivity / higher-latency
 * metadata on Google DLP's `infoTypes.list`.
 */
export async function listPiiInfoTypes(client) {
    const response = await client.get('/pii/info-types');
    return parseSchema(piiInfoTypesListSchema, response);
}
/**
 * List user policies
 */
export async function listUserPolicies(client) {
    const response = await client.get('/account/policies');
    return parseSchema(policyListSchema, response);
}
/**
 * List effective policies based on auth context
 */
export async function listPolicies(client) {
    const response = await client.get('/policies');
    return parseSchema(policyListSchema, response);
}
/**
 * List organization policies
 */
export async function listOrganizationPolicies(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/policies`);
    return parseSchema(policyListSchema, response);
}
/**
 * Toggle a user policy.
 * When mode is provided (enforce_policy_mode feature), sends { mode }; otherwise sends { enabled }.
 */
export async function toggleUserPolicy(client, policyKey, enabled, mode, options) {
    const validated = mode !== undefined
        ? parseSchema(policyToggleSchema, { mode })
        : parseSchema(policyToggleSchema, { enabled });
    const body = { ...validated };
    if (options?.source)
        body.source = options.source;
    if (options?.alertKey)
        body.alertKey = options.alertKey;
    const response = await client.put(`/account/policies/${policyKey}`, body);
    return parseSchema(policyToggleResultSchema, response);
}
/**
 * Get policy settings for a user policy
 */
export async function getUserPolicySettings(client, policyKey) {
    const response = await client.get(`/account/policies/${policyKey}/settings`);
    return parseSchema(policySettingsSchema, response);
}
/**
 * Get edit context for a user (account) policy.
 * Returns the policy mode and an optional single sub-policy setting. Account
 * policies are version-less, so `version` is always 0 (no conflict handling).
 */
export async function getUserPolicyEditContext(client, policyKey, params) {
    const queryParams = new URLSearchParams();
    if (params?.subPolicyKey)
        queryParams.set('subPolicyKey', params.subPolicyKey);
    if (params?.alertKey)
        queryParams.set('alertKey', params.alertKey);
    const query = queryParams.toString();
    const url = `/account/policies/${policyKey}/edit-context${query ? `?${query}` : ''}`;
    const response = await client.get(url);
    return parseSchema(policyEditContextSchema, response);
}
/**
 * Update policy settings for a user policy
 */
export async function updateUserPolicySettings(client, settingsUpdate, options) {
    const validatedData = parseSchema(policySettingsUpdateSchema, settingsUpdate);
    const body = { ...validatedData };
    if (options?.source)
        body.source = options.source;
    if (options?.alertKey)
        body.alertKey = options.alertKey;
    const response = await client.put(`/account/policies/${validatedData.policyKey}/settings`, body);
    return parseSchema(policySettingsUpdateResultSchema, response);
}
/**
 * Delete policy settings for a user policy (reset to defaults)
 */
export async function deleteUserPolicySettings(client, policyKey, settingKey) {
    const path = settingKey
        ? `/account/policies/${policyKey}/settings/${settingKey}`
        : `/account/policies/${policyKey}/settings`;
    const response = await client.delete(path);
    return parseSchema(policySettingsDeleteResultSchema, response);
}
/**
 * Toggle an organization policy.
 * When mode is provided (enforce_policy_mode feature), sends { mode }; otherwise sends { enabled }.
 */
export async function toggleOrganizationPolicy(client, organizationId, policyKey, enabled, mode) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const body = mode !== undefined
        ? parseSchema(policyToggleSchema, { mode })
        : parseSchema(policyToggleSchema, { enabled });
    const response = await client.put(`/organizations/${validatedId}/policies/${policyKey}`, body);
    return parseSchema(policyToggleResultSchema, response);
}
/**
 * Get policy settings for an organization policy
 */
export async function getOrganizationPolicySettings(client, organizationId, policyKey) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/policies/${policyKey}/settings`);
    return parseSchema(policySettingsSchema, response);
}
/**
 * Update policy settings for an organization policy
 */
export async function updateOrganizationPolicySettings(client, organizationId, settingsUpdate) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedData = parseSchema(policySettingsUpdateSchema, settingsUpdate);
    const response = await client.put(`/organizations/${validatedId}/policies/${validatedData.policyKey}/settings`, validatedData);
    return parseSchema(policySettingsUpdateResultSchema, response);
}
/**
 * Delete policy settings for an organization policy (reset to defaults)
 */
export async function deleteOrganizationPolicySettings(client, organizationId, policyKey, settingKey) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const path = settingKey
        ? `/organizations/${validatedId}/policies/${policyKey}/settings/${settingKey}`
        : `/organizations/${validatedId}/policies/${policyKey}/settings`;
    const response = await client.delete(path);
    return parseSchema(policySettingsDeleteResultSchema, response);
}
/**
 * Get edit context for a policy.
 * Returns the policy mode, a single sub-policy setting (if applicable), and version
 * for optimistic concurrency control.
 */
export async function getOrganizationPolicyEditContext(client, organizationId, policyKey, params) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const queryParams = new URLSearchParams();
    if (params?.subPolicyKey)
        queryParams.set('subPolicyKey', params.subPolicyKey);
    if (params?.alertKey)
        queryParams.set('alertKey', params.alertKey);
    const query = queryParams.toString();
    const url = `/organizations/${validatedId}/policies/${policyKey}/edit-context${query ? `?${query}` : ''}`;
    const response = await client.get(url);
    return parseSchema(policyEditContextSchema, response);
}
/**
 * Toggle an organization policy with version check.
 * When expectedVersion is provided, rejects with 409 if version has changed.
 */
export async function toggleOrganizationPolicyVersioned(client, organizationId, policyKey, enabled, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const body = {};
    if (options?.mode !== undefined) {
        body.mode = options.mode;
    }
    else {
        body.enabled = enabled;
    }
    if (options?.expectedVersion !== undefined) {
        body.expectedVersion = options.expectedVersion;
    }
    if (options?.source)
        body.source = options.source;
    if (options?.alertKey)
        body.alertKey = options.alertKey;
    const response = await client.put(`/organizations/${validatedId}/policies/${policyKey}`, body);
    return parseSchema(policyToggleResultSchema, response);
}
/**
 * Update policy settings for an organization with version check.
 * When expectedVersion is provided, rejects with 409 if version has changed.
 */
export async function updateOrganizationPolicySettingsVersioned(client, organizationId, settingsUpdate, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedData = parseSchema(policySettingsUpdateSchema, settingsUpdate);
    const body = {
        ...validatedData,
    };
    if (options?.expectedVersion !== undefined) {
        body.expectedVersion = options.expectedVersion;
    }
    if (options?.source)
        body.source = options.source;
    if (options?.alertKey)
        body.alertKey = options.alertKey;
    const response = await client.put(`/organizations/${validatedId}/policies/${validatedData.policyKey}/settings`, body);
    return parseSchema(policySettingsUpdateResultSchema, response);
}
// ── Security group–scoped policies (KIR-3800) ────────────────────────────────
// Base path: /organizations/:organizationId/security-groups/:groupId/policies
/**
 * List a security group's policies. Each policy shows its effective value and an
 * `isOverridden` flag (group override vs inherited org default).
 */
export async function listGroupPolicies(client, organizationId, groupId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/security-groups/${encodeURIComponent(groupId)}/policies`);
    return parseSchema(policyListSchema, response);
}
/**
 * Set a security group's policy mode, with optimistic concurrency (409 on
 * version conflict when `expectedVersion` is supplied).
 */
export async function toggleGroupPolicyVersioned(client, organizationId, groupId, policyKey, enabled, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const body = {};
    if (options?.mode !== undefined) {
        body.mode = options.mode;
    }
    else {
        body.enabled = enabled;
    }
    if (options?.expectedVersion !== undefined) {
        body.expectedVersion = options.expectedVersion;
    }
    const response = await client.put(`/organizations/${validatedId}/security-groups/${encodeURIComponent(groupId)}/policies/${policyKey}`, body);
    return parseSchema(policyToggleResultSchema, response);
}
/**
 * Get a security group's settings for a policy (effective values + isCustomized).
 */
export async function getGroupPolicySettings(client, organizationId, groupId, policyKey) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/security-groups/${encodeURIComponent(groupId)}/policies/${policyKey}/settings`);
    return parseSchema(policySettingsSchema, response);
}
/**
 * Get the edit context (mode + optional sub-policy + version) for a group policy.
 */
export async function getGroupPolicyEditContext(client, organizationId, groupId, policyKey, params) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const queryParams = new URLSearchParams();
    if (params?.subPolicyKey)
        queryParams.set('subPolicyKey', params.subPolicyKey);
    const query = queryParams.toString();
    const url = `/organizations/${validatedId}/security-groups/${encodeURIComponent(groupId)}/policies/${policyKey}/edit-context${query ? `?${query}` : ''}`;
    const response = await client.get(url);
    return parseSchema(policyEditContextSchema, response);
}
/**
 * Update a security group's policy settings, with optimistic concurrency (409 on
 * version conflict when `expectedVersion` is supplied).
 */
export async function updateGroupPolicySettingsVersioned(client, organizationId, groupId, settingsUpdate, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedData = parseSchema(policySettingsUpdateSchema, settingsUpdate);
    const body = { ...validatedData };
    if (options?.expectedVersion !== undefined) {
        body.expectedVersion = options.expectedVersion;
    }
    const response = await client.put(`/organizations/${validatedId}/security-groups/${encodeURIComponent(groupId)}/policies/${validatedData.policyKey}/settings`, body);
    return parseSchema(policySettingsUpdateResultSchema, response);
}
/**
 * Delete (reset) a security group's policy settings — members fall back to the
 * org default. Omit `settingKey` to reset all of the policy's group settings.
 */
export async function deleteGroupPolicySettings(client, organizationId, groupId, policyKey, settingKey) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const base = `/organizations/${validatedId}/security-groups/${encodeURIComponent(groupId)}/policies/${policyKey}/settings`;
    const path = settingKey ? `${base}/${settingKey}` : base;
    const response = await client.delete(path);
    return parseSchema(policySettingsDeleteResultSchema, response);
}
//# sourceMappingURL=policies.js.map